import gsap from 'gsap';
import throttle from 'lodash.throttle';
import { browser } from '../utils/browsers';

const TWEEN = {
  duration: 0.4,
  ease: 'power1.inout',
  force3D: true,
};

const TWEEN_L = {
  duration: 0.6,
  ease: 'power2.out',
  force3D: true,
};

const TWEEN_EXIT = {
  duration: 5,
  delay: 5,
  force3D: true,
};

const ORIGIN_TOP = {
  transformOrigin: '50% 0%',
};

const ORIGIN_BOT = {
  transformOrigin: '50% 100%',
};

const ANIMTE_FROM = {
  x: '-20%',
  y: '-50%',
  rotationY: 10,
  autoAlpha: 0,
};

const ANIMTE_IN = {
  x: '-50%',
  rotationY: 0,
  autoAlpha: 1,
};

const ANIMTE_OUT = {
  x: '-70%',
  rotationY: 10,
  autoAlpha: 0,
  scale: 0.8,
};

export default class HoverInteraction {
  constructor(domElement, minWidth) {
    this.dom = domElement;
    this.hoverEls = this.dom.querySelectorAll('.js__hover-entry');
    this.displayEls = this.dom.querySelectorAll('.js__hover-entry__image');
    this.activeEl;
    this.clonesContainer = this.dom.querySelector('.js__active-clone');

    this.isResponsive = !!minWidth;
    this.minWidth = minWidth;

    this.isEnabled = false;
    this.hasAttachedListeners = false;

    this.init();
  }

  get shouldEnable() {
    return this.isResponsive ? window.innerWidth > this.minWidth : true;
  }

  init() {
    this.shouldEnable && this.enable();

    this.isResponsive &&
      window.addEventListener(
        'resize',
        () => (this.shouldEnable ? this.enable() : this.disable()),
        { passive: true },
      );
  }

  disable() {
    this.isEnabled = false;
  }

  enable() {
    this.isEnabled = true;

    if (this.hasAttachedListeners) {
      return;
    }

    this.hasAttachedListeners = true;

    this.activeEl = this.dom.querySelector('.js__hover-entry--1');
    this.activeEl.classList.add('active');
    gsap.set(this.activeEl.querySelector('.js__hover-entry__progress'), { scaleY: 1 });
    this.clone();

    this.mouseenter = throttle(this.mouseenter.bind(this), 250);
    this.mouseleave = throttle(this.mouseleave.bind(this), 250);
    this.autoplay = this.mouseleave;

    this.dom.querySelectorAll('.js__hover-entry .js__hover-entry__text').forEach(el => {
      el.addEventListener('mouseenter', () => this.mouseenter(el.parentNode));
      el.addEventListener('mouseleave', () => this.mouseleave());
    });

    this.autoplay();
  }

  mouseenter(el) {
    if (this.isEnabled) {
      if (el === this.activeEl) {
        return;
      }
      gsap.killTweensOf(this.activeEl.querySelector('.js__hover-entry__progress'));
      gsap.to(this.activeEl.querySelector('.js__hover-entry__progress'), {
        ...TWEEN,
        ...ORIGIN_BOT,
        scaleY: 0,
      });
      this.play(el);
    }
  }

  mouseleave() {
    if (this.isEnabled) {
      gsap.killTweensOf(this.activeEl.querySelector('.js__hover-entry__progress'));
      gsap.to(this.activeEl.querySelector('.js__hover-entry__progress'), {
        ...TWEEN,
        ...ORIGIN_TOP,
        scaleY: 1,
      });
      gsap.to(this.activeEl.querySelector('.js__hover-entry__progress'), {
        ...TWEEN_EXIT,
        ...ORIGIN_BOT,
        scaleY: 0,
        onComplete: () => {
          let next = this.activeEl.getAttribute('data-index');
          if (next >= this.hoverEls.length) {
            next = 0;
          }
          this.play(this.hoverEls[next]);
        },
      });
    }
  }

  play(el) {
    if (this.isEnabled) {
      this.removeClone();
      this.activeEl?.classList.remove('active');
      this.activeEl = el;
      this.activeEl.classList.add('active');
      this.clone();
      gsap.to(this.activeEl.querySelector('.js__hover-entry__progress'), {
        ...TWEEN,
        ...ORIGIN_TOP,
        scaleY: 1,
        onComplete: () => {
          this.autoplay();
        },
      });
    }
  }

  clone() {
    const cloneEl = this.displayEls[this.activeEl.getAttribute('data-index') - 1].cloneNode(true);
    cloneEl.classList.add('js__hover-entry__clone');
    this.clonesContainer.appendChild(cloneEl);
    if (browser.name !== 'Safari') {
      gsap.fromTo(cloneEl, ANIMTE_FROM, { ...TWEEN_L, ...ANIMTE_IN });
    } else {
      gsap.fromTo(
        cloneEl,
        { y: '-50%', x: '-50%', autoAlpha: 0, force3D: true },
        { ...TWEEN_L, autoAlpha: 1 },
      );
    }
  }

  removeClone() {
    this.dom.querySelectorAll('.js__hover-entry__clone').forEach(el => {
      if (!gsap.isTweening(el)) {
        if (browser.name !== 'Safari') {
          gsap.to(el, { ...TWEEN, ...ANIMTE_OUT, onComplete: () => el.remove() });
        } else {
          gsap.to(el, { ...TWEEN, autoAlpha: 0, force3D: true, onComplete: () => el.remove() });
        }
      } else {
        el.remove();
      }
    });
  }
}
